import { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { fetchEspecialistas } from '../../services/especialistas';
import { useAuth } from '../../contexts/AuthContext';

function AtendimentoEspecialistaModal({
  isOpen,
  onClose,
  atendimento,
  movimentacaoId,
  movimentacaoFinalizada,
  onSaveSuccess,
  especialistas = [],
  adolescenteNome,
  theme = 'dark',
}) {
  const { hasPermission, hasAnyPermission } = useAuth();

  const canEdit = hasAnyPermission(['atendimentos.create', 'atendimentos.edit']) && !movimentacaoFinalizada;
  const canViewOnly = !canEdit && hasPermission('atendimentos.view');
  const isReadOnly = canViewOnly;

  // Lista local de especialistas (usada quando o pai não passa nada — ex.: Dashboard)
  const [localEspecialistas, setLocalEspecialistas] = useState([]);
  const [especialistasLoaded, setEspecialistasLoaded] = useState(false);

  const [formData, setFormData] = useState({
    id_especialista: '',
    check_sei: false,
    check_entrada: false,
    check_saida: false,
    responsavel_presente: false,
    responsavel_nome: '',
    responsavel_observacao: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Fonte final de especialistas: prioridade para o que vem do pai,
  // se vier vazio, usamos a lista carregada internamente.
  const especialistasOptions = especialistas.length > 0 ? especialistas : localEspecialistas;
  const showEspecialistasLoading =
    canEdit && !especialistasLoaded && especialistasOptions.length === 0;
  const sortedEspecialistasOptions = useMemo(() => {
    const list = [...especialistasOptions];

    list.sort((a, b) => {
      const aIsSemAtendimento = Number(a?.id) === 1;
      const bIsSemAtendimento = Number(b?.id) === 1;

      if (aIsSemAtendimento && !bIsSemAtendimento) return -1;
      if (bIsSemAtendimento && !aIsSemAtendimento) return 1;

      const nomeA = String(a?.nome ?? '');
      const nomeB = String(b?.nome ?? '');
      return nomeA.localeCompare(nomeB, 'pt-BR', { sensitivity: 'base' });
    });

    return list;
  }, [especialistasOptions]);

  useEffect(() => {
    if (atendimento) {
      setFormData({
        id_especialista: atendimento.id_especialista || '',
        check_sei: !!atendimento.check_sei,
        check_entrada: !!atendimento.check_entrada,
        check_saida: !!atendimento.check_saida,
        responsavel_presente: !!atendimento.responsavel_presente,
        responsavel_nome: atendimento.responsavel_nome || '',
        responsavel_observacao: atendimento.responsavel_observacao || '',
      });
    } else if (isOpen) {
      setFormData({
        id_especialista: '',
        check_sei: false,
        check_entrada: false,
        check_saida: false,
        responsavel_presente: false,
        responsavel_nome: '',
        responsavel_observacao: '',
      });
    }
  }, [atendimento, isOpen]);

  // Carrega Especialistas localmente quando a modal abre e o pai não passou lista (ex.: Dashboard)
  useEffect(() => {
    if (!isOpen) {
      setEspecialistasLoaded(false);
      return;
    }

    if (especialistas.length > 0 || localEspecialistas.length > 0) {
      setEspecialistasLoaded(true);
    }
  }, [isOpen, especialistas.length, localEspecialistas.length]);

  useEffect(() => {
    if (!isOpen || !canEdit) return;
    if (especialistas.length > 0 || localEspecialistas.length > 0) return;

    setEspecialistasLoaded(false);
    fetchEspecialistas()
      .then(setLocalEspecialistas)
      .catch(() => {
        setLocalEspecialistas([]);
      })
      .finally(() => {
        setEspecialistasLoaded(true);
      });
  }, [isOpen, canEdit, especialistas.length, localEspecialistas.length]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;

    setFormData((prev) => {
      const next = { ...prev, [name]: val };
      // Cascata: se desmarcar entrada, desmarca saída
      if (name === 'check_entrada' && !val) next.check_saida = false;
      return next;
    });

    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isReadOnly) return;

    if (!String(formData.id_especialista ?? '').trim()) {
      setValidationErrors({ id_especialista: ['Selecione um especialista.'] });
      return;
    }

    setSaving(true);
    setError(null);
    setValidationErrors({});

    try {
      if (atendimento) {
        await api.put(`/atendimentos-especialistas/${atendimento.id}`, formData);
      } else {
        await api.post(`/movimentacoes/${movimentacaoId}/atendimentos-especialistas`, formData);
      }
      onSaveSuccess && onSaveSuccess('Atendimento do Especialista salvo com sucesso!');
      onClose();
    } catch (err) {
      const msg =
        err.userMessage ||
        err.response?.data?.message ||
        'Ocorreu um erro ao salvar o atendimento.';
      setError(msg);
      if (err.validationErrors) setValidationErrors(err.validationErrors);
      if (!err.silence) console.error('Erro ao salvar atendimento de Especialistas:', err);
    } finally {
      setSaving(false);
    }
  };

  const getEspecialistaNome = () => {
    // Prioridade 1: Nome que vem do backend (funciona mesmo com especialista excluído)
    if (atendimento?.especialista_nome) {
      return atendimento.especialista_nome;
    }
    
    // Prioridade 2: Busca na lista de especialistas ativos
    if (!formData.id_especialista) return '—';
    const esp = sortedEspecialistasOptions.find((e) => e.id === Number(formData.id_especialista));
    return esp?.nome || '—';
  };

  const simNao = (flag) => {
    if (typeof flag === 'boolean') return flag ? 'Sim' : 'Não';
    return flag ? 'Sim' : 'Não';
  };

  const renderResumoSomenteLeitura = () => (
    <div className="mt-2">
      <div className="mb-2">
        <strong>Especialista:</strong>{' '}
        <span className="text-light">{getEspecialistaNome()}</span>
      </div>

      <hr className="my-3" />

      <div className="mb-2">
        <strong>Responsável Presente:</strong>{' '}
        <span className={formData.responsavel_presente ? 'text-success' : 'text-secondary'}>
          {simNao(formData.responsavel_presente)}
        </span>
      </div>
      {formData.responsavel_nome && (
        <div className="mb-2">
          <strong>Nome do Responsável:</strong>{' '}
          <span className="text-light">{formData.responsavel_nome}</span>
        </div>
      )}
      {formData.responsavel_observacao && (
        <div className="mb-2">
          <strong>Observações:</strong>{' '}
          <span className="text-light">{formData.responsavel_observacao}</span>
        </div>
      )}

      <hr className="my-3" />

      <div className="mb-2">
        <strong>Atendimento Iniciado:</strong>{' '}
        <span className={formData.check_entrada ? 'text-success' : 'text-secondary'}>
          {simNao(formData.check_entrada)}
        </span>
      </div>
      <div className="mb-2">
        <strong>Atendimento Finalizado:</strong>{' '}
        <span className={formData.check_saida ? 'text-success' : 'text-secondary'}>
          {simNao(formData.check_saida)}
        </span>
      </div>
      <div className="mb-2">
        <strong>Relatório SEI Concluído:</strong>{' '}
        <span className={formData.check_sei ? 'text-success' : 'text-secondary'}>
          {simNao(formData.check_sei)}
        </span>
      </div>

      <div className="mt-3 small text-secondary">
        Visualização somente. Permissão de edição não concedida para este usuário.
      </div>
    </div>
  );

  return (
    <div className={`modal fade ${isOpen ? 'show d-block' : ''} dashboard-themed-modal dashboard-themed-modal--${theme}`} tabIndex="-1">
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content bg-dark text-light dashboard-themed-modal-content">
          <div className="modal-header border-secondary">
            <h5 className="modal-title atendimento-modal-title">Atendimento - Especialistas</h5>
            <button
              type="button"
              className={theme === 'light' ? 'btn-close' : 'btn-close btn-close-white'}
              onClick={onClose}
              aria-label="Fechar"
            />
          </div>

          <div className="modal-body">
            {adolescenteNome && (
              <>
                <hr className="my-2" />
                <p className="text-light mb-3">
                  <strong>Adolescente:</strong> {adolescenteNome}
                </p>
              </>
            )}

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {isReadOnly ? (
              renderResumoSomenteLeitura()
            ) : (
              <form id="form-especialista" onSubmit={handleSubmit}>
                {/* Especialista */}
                <div className="mb-3">
                  <label htmlFor="id_especialista" className="form-label">
                    Especialista <span className="text-danger">*</span>
                  </label>
                  <select
                    id="id_especialista"
                    name="id_especialista"
                    className={`form-select ${validationErrors.id_especialista ? 'is-invalid' : ''}`}
                    value={formData.id_especialista}
                    onChange={handleChange}
                    disabled={showEspecialistasLoading}
                  >
                    {showEspecialistasLoading ? (
                      <option value="">Carregando...</option>
                    ) : (
                      <>
                        <option value="">Selecione...</option>
                        {sortedEspecialistasOptions.map((e) => (
                          <option key={e.id} value={e.id}>
                            {e.nome}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {validationErrors.id_especialista && (
                    <div className="invalid-feedback">{validationErrors.id_especialista[0]}</div>
                  )}
                </div>

                <hr className="my-3" />

                {/* Responsável Presente */}
                <div className="mb-3 form-check form-switch">
                  <input
                    type="checkbox"
                    id="responsavel_presente"
                    name="responsavel_presente"
                    className="form-check-input"
                    checked={formData.responsavel_presente}
                    onChange={handleChange}
                  />
                  <label htmlFor="responsavel_presente" className="form-check-label">
                    Responsável Presente
                  </label>
                </div>

                {/* Nome do Responsável - Visível apenas se responsável presente */}
                {formData.responsavel_presente && (
                  <div className="mb-3">
                    <label htmlFor="responsavel_nome" className="form-label">
                      Nome do Responsável <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="responsavel_nome"
                      name="responsavel_nome"
                      className={`form-control ${validationErrors.responsavel_nome ? 'is-invalid' : ''}`}
                      value={formData.responsavel_nome}
                      onChange={handleChange}
                      placeholder="Nome completo do responsável"
                      required={formData.responsavel_presente}
                    />
                    {validationErrors.responsavel_nome && (
                      <div className="invalid-feedback">{validationErrors.responsavel_nome[0]}</div>
                    )}
                  </div>
                )}

                {/* Observações */}
                <div className="mb-3">
                  <label htmlFor="responsavel_observacao" className="form-label">
                    Observações
                  </label>
                  <textarea
                    id="responsavel_observacao"
                    name="responsavel_observacao"
                    className={`form-control ${validationErrors.responsavel_observacao ? 'is-invalid' : ''}`}
                    rows="3"
                    value={formData.responsavel_observacao}
                    onChange={handleChange}
                    placeholder="Observações gerais sobre o responsável..."
                  />
                  {validationErrors.responsavel_observacao && (
                    <div className="invalid-feedback">{validationErrors.responsavel_observacao[0]}</div>
                  )}
                </div>

                <hr className="my-3" />

                {/* Checkboxes de Status */}
                <div className="mb-3 form-check form-switch">
                  <input
                    type="checkbox"
                    id="check_entrada"
                    name="check_entrada"
                    className="form-check-input"
                    checked={formData.check_entrada}
                    onChange={handleChange}
                  />
                  <label htmlFor="check_entrada" className="form-check-label">
                    Atendimento Iniciado
                  </label>
                </div>

                <div className="mb-3 form-check form-switch">
                  <input
                    type="checkbox"
                    id="check_saida"
                    name="check_saida"
                    className="form-check-input"
                    checked={formData.check_saida}
                    onChange={handleChange}
                    disabled={!formData.check_entrada}
                  />
                  <label htmlFor="check_saida" className="form-check-label">
                    Atendimento Finalizado
                  </label>
                </div>

                {!formData.check_entrada && (
                  <small className="text-secondary d-block mb-3">
                    Para finalizar, o atendimento deve estar &quot;Iniciado&quot;.
                  </small>
                )}

                <div className="mb-3 form-check form-switch">
                  <input
                    type="checkbox"
                    id="check_sei"
                    name="check_sei"
                    className="form-check-input"
                    checked={formData.check_sei}
                    onChange={handleChange}
                  />
                  <label htmlFor="check_sei" className="form-check-label">
                    Relatório SEI Concluído
                  </label>
                </div>
              </form>
            )}
          </div>

          <div className="modal-footer border-secondary">
            {isReadOnly ? (
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Fechar
              </button>
            ) : (
              <>
                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AtendimentoEspecialistaModal;
